import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Users, Pencil, Trash2, AlertTriangle, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROLES = ['Administrador', 'Redator Sênior', 'Redator Padrão', 'Leitor/Gestor'];
const ATTRIBUTES = ['Editor de Preços'];

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'Redator Padrão', attributes: [] });
  const [editData, setEditData] = useState({ role: '', attributes: [], is_active: true });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/users`, newUser, { withCredentials: true });
      toast.success('Usuário criado com sucesso');
      setCreateOpen(false);
      setNewUser({ email: '', name: '', role: 'Redator Padrão', attributes: [] });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditData({ role: user.role, attributes: user.attributes || [], is_active: user.is_active !== false });
  };

  const handleEdit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/users/${editUser.user_id}`, editData, { withCredentials: true });
      toast.success('Usuário atualizado');
      setEditUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/users/${userId}`, { withCredentials: true });
      toast.success('Usuário excluído');
      setDeleteTarget(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const toggleAttribute = (attr, list, setter) => {
    setter(list.includes(attr) ? list.filter(a => a !== attr) : [...list, attr]);
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Gestão de Usuários</h1>
          <p className="text-[#6E6E73]">Gerenciar perfis e permissões</p>
        </div>
        <Button onClick={() => { setNewUser({ email: '', name: '', role: 'Redator Padrão', attributes: [] }); setCreateOpen(true); }} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-user-button">
          <UserPlus className="w-4 h-4 mr-2" />Novo Usuário
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F7F7F8]">
            <tr>
              <th className="text-left p-4 text-xs font-medium text-[#6E6E73]">USUÁRIO</th>
              <th className="text-left p-4 text-xs font-medium text-[#6E6E73]">E-MAIL</th>
              <th className="text-left p-4 text-xs font-medium text-[#6E6E73]">PERFIL</th>
              <th className="text-left p-4 text-xs font-medium text-[#6E6E73]">ATRIBUTOS</th>
              <th className="text-center p-4 text-xs font-medium text-[#6E6E73]">STATUS</th>
              <th className="text-right p-4 text-xs font-medium text-[#6E6E73]">AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-[#E5E5EA] hover:bg-[#F7F7F8]" data-testid={`user-row-${u.user_id}`}>
                <td className="p-4 text-sm font-medium">{u.name || 'N/A'}</td>
                <td className="p-4 text-sm text-[#6E6E73]">{u.email}</td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${u.role === 'Administrador' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  {(u.attributes || []).map(a => <span key={a} className="text-xs bg-[#F0FDF4] text-green-700 px-2 py-0.5 rounded mr-1">{a}</span>)}
                </td>
                <td className="p-4 text-center">
                  <span className={`text-xs px-2 py-1 rounded ${u.is_active !== false ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {u.is_active !== false ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(u)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Novo Usuário</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="create-user-form">
            <div>
              <Label>Nome *</Label>
              <Input required value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div>
              <Label>E-mail *</Label>
              <Input required type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div>
              <Label>Perfil *</Label>
              <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Atributos</Label>
              <div className="flex gap-2 mt-1">
                {ATTRIBUTES.map(attr => (
                  <button key={attr} type="button"
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${newUser.attributes.includes(attr) ? 'bg-[#AF2628] text-white border-[#AF2628]' : 'bg-white text-[#6E6E73] border-[#E5E5EA]'}`}
                    onClick={() => {
                      const attrs = newUser.attributes.includes(attr) ? newUser.attributes.filter(a => a !== attr) : [...newUser.attributes, attr];
                      setNewUser({ ...newUser, attributes: attrs });
                    }}>
                    {attr}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="create-user-submit">
                {submitting ? 'Criando...' : 'Criar Usuário'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar Usuário - {editUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Perfil</Label>
              <Select value={editData.role} onValueChange={(val) => setEditData({ ...editData, role: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Atributos</Label>
              <div className="flex gap-2 mt-1">
                {ATTRIBUTES.map(attr => (
                  <button key={attr} type="button"
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${editData.attributes.includes(attr) ? 'bg-[#AF2628] text-white border-[#AF2628]' : 'bg-white text-[#6E6E73] border-[#E5E5EA]'}`}
                    onClick={() => {
                      const attrs = editData.attributes.includes(attr) ? editData.attributes.filter(a => a !== attr) : [...editData.attributes, attr];
                      setEditData({ ...editData, attributes: attrs });
                    }}>
                    {attr}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editData.is_active ? 'true' : 'false'} onValueChange={(val) => setEditData({ ...editData, is_active: val === 'true' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Ativo</SelectItem>
                  <SelectItem value="false">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditUser(null)}>Cancelar</Button>
              <Button className="bg-[#AF2628] hover:bg-[#961B22]" onClick={handleEdit} disabled={submitting}>
                {submitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Excluir Usuário</DialogTitle>
            <DialogDescription>Excluir o usuário <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.user_id)}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
